import _ from 'lodash';
import { getCapsuleFormula } from '@/datetime/dateTime.utilities';
import { EMPTY_XY_REGION } from '@/scatterPlot/scatterPlot.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { toNumber } from '@/utilities/numberHelper.utilities';
import type { ScatterPlotSignal, XYPlotRegion } from '@/scatterPlot/scatterPlot.constants';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { RangeExport } from '@/trendData/duration.store';

interface FormulaParameter {
  name: string;
  item: { id: string };
}

interface FormulaResult {
  formula: string;
  formulaParameters: { xSignal: string; ySignal: string };
}

export const DEFAULT_DURATION: FrontendDuration = { value: 0, units: 'min' };

interface ScatterConditionState extends BaseToolPersistedState {
  isBounding: boolean;
  isCleansing: boolean;
  minDuration: FrontendDuration;
  mergeDuration: FrontendDuration;
}

export type ScatterConditionDehydratedState = ScatterConditionState;

export class ScatterConditionStore extends BaseToolStore {
  static readonly storeName = 'sqScatterConditionStore';
  type = TREND_TOOLS.SCATTER_CONDITION;

  initialize(): void {
    const initialState: ScatterConditionState = {
      ...BASE_TOOL_COMMON_PROPS,
      isBounding: false,
      isCleansing: false,
      minDuration: DEFAULT_DURATION,
      mergeDuration: DEFAULT_DURATION,
    };
    this.state = this.immutable(initialState);
  }

  get isBounding(): boolean {
    return this.state.get('isBounding');
  }

  get isCleansing(): boolean {
    return this.state.get('isCleansing');
  }

  get minDuration(): FrontendDuration {
    return this.state.get('minDuration');
  }

  get mergeDuration(): FrontendDuration {
    return this.state.get('mergeDuration');
  }

  getSelectedRegionFromFormula(formula: string, parameters: FormulaParameter[]): XYPlotRegion {
    const parsedXMin = toNumber(_.get(/(\$xSignal > (-?[0-9]+([,.]?)[0-9]*))/.exec(formula), '2'));
    const parsedXMax = toNumber(_.get(/(\$xSignal < (-?[0-9]+([,.]?)[0-9]*))/.exec(formula), '2'));
    const parsedYMin = toNumber(_.get(/(\$ySignal > (-?[0-9]+([,.]?)[0-9]*))/.exec(formula), '2'));
    const parsedYMax = toNumber(_.get(/(\$ySignal < (-?[0-9]+([,.]?)[0-9]*))/.exec(formula), '2'));

    const parseForMerge = /merge\(([0-9]+([,.]?)[0-9]*)([a-z]+)/.exec(formula);
    const maybeMergeNumber = _.get(parseForMerge, '1') ? toNumber(_.get(parseForMerge, '1')) : null;
    const maybeMergeUnit = _.get(parseForMerge, '3');

    if (maybeMergeNumber && maybeMergeUnit) {
      this.state.set('mergeDuration', { value: maybeMergeNumber, units: maybeMergeUnit });
    }

    const parseForMin = /removeShorterThan\(([0-9]+([,.]?)[0-9]*)([a-z]+)/.exec(formula);
    const maybeMinNumber = _.get(parseForMin, '1') ? toNumber(_.get(parseForMin, '1')) : null;
    const maybeMinUnit = _.get(parseForMin, '3');

    if (maybeMinNumber && maybeMinUnit) {
      this.state.set('minDuration', { value: maybeMinNumber, units: maybeMinUnit });
      this.state.set('isCleansing', true);
    }

    const yParameter = _.find(parameters, (parameter) => parameter.name === 'ySignal');
    const yId = yParameter?.item?.id;

    const xMin = parsedXMin ?? null;
    const xMax = parsedXMax ?? null;
    const yMin = parsedYMin ?? null;
    const yMax = parsedYMax ?? null;

    if (!yId) {
      return {
        x: { min: xMin, max: xMax },
        ys: {},
      };
    }

    return {
      x: { min: xMin, max: xMax },
      ys: { [yId]: { min: yMin, max: yMax } },
    };
  }

  /**
   * Create a condition formula representing a region on the scatter plot.
   * The basic formula consists of two intersecting value searches.
   *
   * @param region - region on the scatter plot to search within
   * @param xSeries - signal on the x-axis of the scatter plot
   * @param ySeries - signal on the y-axis of the scatter plot
   * @param isBounding - whether or not to only create capsules within the display range
   * @param {moment} displayRange - time range displayed on the scatter plot. Used if isBounding is true.
   * @param isCleansing
   * @param minDuration
   * @param mergeDuration
   */
  getFormulaAndParameters(
    region: XYPlotRegion,
    xSeries: ScatterPlotSignal | null,
    ySeries: ScatterPlotSignal | null,
    isBounding: boolean,
    displayRange: RangeExport | undefined,
    isCleansing: boolean,
    minDuration: ValueWithUnitsItem,
    mergeDuration: ValueWithUnitsItem,
  ): FormulaResult {
    if (!xSeries || !ySeries) {
      throw new Error('Expected series to be present on both axes of the scatter plot.');
    }

    if (!region || _.isEqual(region, EMPTY_XY_REGION)) {
      throw new Error('Expected a region to be selected on the scatter plot, but found no selected region.');
    }

    const yRange =
      region.ys[ySeries.id] ??
      // TODO CRAB-27993: Should always have the correct ID here
      _.chain(region.ys).toArray().head().value();
    const xMin = region.x.min;
    const xMax = region.x.max;
    const yMin = yRange?.min;
    const yMax = yRange?.max;

    if (xMin === null || xMax === null || yMin === null || yMax === null || xMax < xMin || yMax < yMin) {
      throw new Error('Expected maximum value to be greater than minimum value.');
    }

    const formulaParameters = {
      xSignal: xSeries.id,
      ySignal: ySeries.id,
    };
    const xSignalFormula = '$xSignal';
    const ySignalFormula = '$ySignal';

    let formula =
      `(${xSignalFormula} > ${xMin} ` +
      `\nand ${xSignalFormula} < ${xMax})` +
      `\nand (${ySignalFormula} > ${yMin} ` +
      `\nand ${ySignalFormula} < ${yMax})`;

    if (isBounding) {
      if (!displayRange) {
        throw new Error('Expected a display range when bounding capsules.');
      }
      const capsuleFormula = getCapsuleFormula(displayRange);
      const conditionFormula = `condition(${displayRange.duration.asMilliseconds()} ms, ${capsuleFormula})`;
      formula = `(${formula})\nand ${conditionFormula}`;
    }

    if (isCleansing) {
      formula = `(${formula})`;
      formula += mergeDuration.value > 0 ? `\n.merge(${mergeDuration.value}${mergeDuration.units})` : '';
      formula += minDuration.value > 0 ? `\n.removeShorterThan(${minDuration.value}${minDuration.units})` : '';
    }
    return { formula, formulaParameters };
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): ScatterConditionDehydratedState {
    return {
      ...this.getBasePersistedState(),
      isBounding: Boolean(this.state.get('isBounding')),
      isCleansing: Boolean(this.state.get('isCleansing')),
      minDuration: this.state.get('minDuration') as FrontendDuration,
      mergeDuration: this.state.get('mergeDuration') as FrontendDuration,
    };
  }

  /**
   * Sets the references panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<ScatterConditionDehydratedState>): void {
    this.applyBasePersistedState(dehydratedState);

    if ('isBounding' in dehydratedState && dehydratedState.isBounding !== undefined) {
      this.state.set('isBounding', dehydratedState.isBounding);
    }
    if ('isCleansing' in dehydratedState && dehydratedState.isCleansing !== undefined) {
      this.state.set('isCleansing', dehydratedState.isCleansing);
    }
    if ('minDuration' in dehydratedState && dehydratedState.minDuration !== undefined) {
      this.state.set('minDuration', dehydratedState.minDuration);
    }
    if ('mergeDuration' in dehydratedState && dehydratedState.mergeDuration !== undefined) {
      this.state.set('mergeDuration', dehydratedState.mergeDuration);
    }
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    /**
     * Set whether or not we want to create capsules only within the display range
     *
     * @param payload - Object container for arguments
     * @param payload.isBounding - whether to limit the condition to within the display range
     */
    SCATTER_CONDITION_SET_BOUNDING: (payload: { isBounding: boolean }) => {
      this.state.set('isBounding', payload.isBounding);
    },

    /**
     * Set whether or not we want to ignore short capsules/gaps
     *
     * @param payload - Object container for arguments
     * @param payload.isCleansing - whether to ignore short capsules/gaps
     */
    SCATTER_CONDITION_SET_CLEANSING: (payload: { isCleansing: boolean }) => {
      this.state.set('isCleansing', payload.isCleansing);
    },
    /**
     * Set the minimum capsule duration to allow
     *
     * @param payload - Object container for arguments
     * @param payload.minDuration - minimum allowable capsule duration
     */
    SCATTER_CONDITION_SET_MIN_DURATION: (payload: { minDuration: FrontendDuration }) => {
      this.state.set('minDuration', payload.minDuration);
    },
    /**
     * Set the maximum gap duration over which to merge capsules
     *
     * @param payload - Object container for arguments
     * @param payload.mergeDuration - maximum duration to merge capsules over a gap
     */
    SCATTER_CONDITION_SET_MERGE_DURATION: (payload: { mergeDuration: FrontendDuration }) => {
      this.state.set('mergeDuration', payload.mergeDuration);
    },
  };
}
